import type { LinkSchema } from '../types'
import { defu } from 'defu'
import { navigateTo, useRouter } from '#imports'

/**
 * Utility for creating dynamic links based on NuxtLink's props.
 *
 * **IMPORTANT**
 *
 * When creating links with a hash parameter, makes sure to use the `hash` property to add the hash to the link, otherwise it will be ignored. E.g. don't add it in the `path` property.
 *
 * @param schemaGetter A function for retrieving the schema.
 */
export function createLink<Options extends Omit<LinkSchema['to'], 'path' | 'name'>>(schemaGetter: () => LinkSchema) {
    const router = useRouter()

    return {
        /**
         * Get props for link. Useful for v-binding on a NuxtLink or UIButton.
         * @param options Override route options.
         */
        props: (options: Options = {} as Options): {
            to?: LinkSchema['to']
            href?: string
        } & LinkSchema['options'] => {
            const schema = schemaGetter()

            const to = defu(options, schema.to)

            let preparedSchema: {
                to?: LinkSchema['to']
                href?: string
            } & LinkSchema['options'] = {}

            // Handle cases with a hash, since NuxtLink doesn't render the url with the hash by default in Nuxt 3.7.
            if (to.hash) {
                const route = router.resolve(to)
                preparedSchema.href = route.fullPath
            }
            else {
                preparedSchema.to = to
            }

            // Add NuxtLink options
            if (schema.options) {
                preparedSchema = Object.assign(preparedSchema, schema.options)
            }

            return preparedSchema
        },
        /**
         * Navigate to link. Useful for navigating programatically in a component, composable or plugin.
         * @param options Override route options.
         */
        navigate: async (options: Options = {} as Options) => {
            const schema = schemaGetter()

            const to = defu(options, schema.to)

            await navigateTo(to, schema.options)
        },
    }
}
